/**
 * @fileoverview One class's sessions — every day it meets, and whether that
 * day has notes.
 *
 * Lives under its program (I8): `/instructors/:userId/programs/:programId/
 * classes/:classId`. A class is reached from its program and goes back to it,
 * and the Programs item stays lit while it is open. The old
 * `/classes/:id/sessions` address still works — a program class is sent on to
 * its new one, a personal class is served there.
 *
 * This is the screen the program flow is actually for. A class that meets
 * twenty times is twenty separate teaching records, and the only question the
 * instructor has, week after week, is *which ones have I not written up*. So
 * that is the question the page answers first: a past session either has notes
 * or visibly does not, and the missing ones carry the only action on the page.
 *
 * **What the org asked for rides beside each session (O7).** Its note to the
 * instructor and the lesson plan it attached are read here, in the composer
 * and on the written-up lesson, and never by a family. The class's curriculum
 * (O10) sits at the bottom with a quick link from the top: every revision, the
 * org's and the instructor's own, labelled by who uploaded it.
 *
 * **The same three tabs as the organization's class page** — Overview,
 * Calendar, Lessons — because a class is asked the same questions from either
 * side: how is it going, when does it meet, what has been taught. What differs
 * is the answer. There is no "taught by" line (on this side, that is you); the
 * header and Overview carry what an instructor needs on the way in — the room,
 * the venue, whatever the org said is unusual — and the actions carry what only
 * the instructor can do. Students and Settings stay the organization's.
 *
 * **Prep notes and lesson notes are one document** (lib/prepNotes). A session
 * more than five minutes from starting takes a plan; from then on the same
 * text is its write-up. Every surface here that opens a session — the Overview
 * list, the calendar, the Lessons tab, the Add prep notes picker — sends it to
 * the same place for the same state, so no two of them can disagree.
 *
 * **Cancelled sessions ask for nothing.** They stay listed — a snow day is part
 * of the record and deleting it would make the term look shorter than it was —
 * but they are never counted as missing notes. The mirror does not yet report
 * cancellations (the teaching view carries confirmed appointments only), so
 * `status: "cancelled"` arrives from no real source today; the row is kept
 * because the alternative, once it does, is a snow day silently owing notes.
 *
 * **Notes reach the program through the occurrence, not the class.** Writing
 * against a session stamps the note with the occurrence, which carries the
 * program; that is what makes it visible to the organization. Attribution stays
 * schedule-only, exactly as Spec 12 requires — the class's own `program_id` is
 * a denormalized convenience for queries, never the authority.
 *
 * **Substitutes (docs/substitute-requests.md).** When the program allows it, a
 * "Request a sub" action asks the organization to cover upcoming sessions. A
 * session somebody else covers stays on the page — greyed, "Covered by …" —
 * but it is not the reader's: never missing notes, never counted as taught,
 * and its lesson opens read-only. A session the reader covers for a colleague
 * reads "Covering" and owes notes like any other.
 *
 * **Two horizons, deliberately.** `/classes/:id/sessions` merges what the
 * scheduling cache still holds (the last four weeks and the next, gaps
 * included) with every past session that has a durable teaching record, however
 * old. So an old written-up session stays listed after its appointment has
 * aged out, and an old *gap* does not — only the cache could ever have known
 * about it, and claiming otherwise would invent a backlog.
 */

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { getErrorMessage } from "@/lib/api";
import ClassStudentsTab from "@/components/ClassStudentsTab";
import UpdatesInbox from "@/components/UpdatesInbox";
import { composeHrefForOccurrence } from "@/lib/schedule";
import { programClassPath, programDetailPath } from "@/lib/programsRoute";
import { curriculumRevisions } from "@/lib/materials";
import { useTabParam } from "@/lib/tabParam";
import { STATUSES, filterLessons, lessonStatus, missingRow, sortChronologically } from "@/lib/lessonHistory";
import useInstructorNotesClear from "@/components/studio/useInstructorNotesClear";
import { clearanceLine, clearanceTag, isCleared } from "@/lib/notesClearance";
import { isPrepWindow, prepActionLabel } from "@/lib/prepNotes";
import { AuthContext } from "@/App";
import CurriculumCard from "@/components/CurriculumCard";
import ResourceTiles from "@/components/ResourceTiles";
import SessionBrief from "@/components/SessionBrief";
import MeetingCalendar, { weekStart } from "@/components/org/MeetingCalendar";
import LessonFilterBar from "@/components/studio/LessonFilterBar";
import PrepSessionPicker from "@/components/studio/PrepSessionPicker";
import RequestSubDialog from "@/components/studio/RequestSubDialog";
import { REQUEST_STATUS, coverName } from "@/lib/substitutes";
import {
  Card, PageShell, SectionHeader, SegmentedControl, Tag, Button, Icon, ListRow, StatCard,
} from "@/components/studio/primitives";

const KIND_LABELS = { studio_class: "Class", ensemble: "Ensemble", cohort: "Cohort" };

/* The org's class page asks five questions; three are the instructor's. Who is
   enrolled and how the class is set up stay with the organization that owns
   them — the roster is one tap away through the Students tile. */
const TABS = [
  { id: "overview", label: "Overview", testId: "class-tab-overview" },
  { id: "calendar", label: "Calendar", testId: "class-tab-calendar" },
  { id: "students", label: "Students", testId: "class-tab-students" },
  { id: "lessons", label: "Lessons", testId: "class-tab-lessons" },
  /* What the organization said about this class — its announcements, a
     cancelled meeting, a chase for notes — the program's Updates narrowed to
     one class. Only for a program class: a personal one has no organization. */
  { id: "updates", label: "Updates", testId: "class-tab-updates" },
];

/* How many meetings the Overview lists before handing over to the Calendar.
   About a fortnight of a twice-weekly class: enough to plan against, short
   enough that the room and the address are still on screen. */
const OVERVIEW_UPCOMING = 5;

/** A session a substitute covers for somebody else — never the reader's to write. */
const coveredByOther = (s) => Boolean(s?.covered && !s.covered.mine);

/** One line for the venue, or nothing. */
function addressLine(location) {
  if (!location) return null;
  return (
    [location.address_line1, location.address_line2, location.city, location.region, location.postal_code]
      .filter(Boolean)
      .join(", ") || null
  );
}

function when(iso, opts = { month: "short", day: "numeric" }) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, opts);
}

function sessionLabel(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function clock(timeLocal) {
  const [h, m] = (timeLocal || "").split(":").map(Number);
  if (Number.isNaN(h)) return timeLocal || "";
  const d = new Date();
  d.setHours(h, m || 0, 0, 0);
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export default function ClassSessionsPage() {
  const { classId, userId, programId } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { user } = useContext(AuthContext) || {};
  const [data, setData] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState(false);
  /* The Request a sub dialog, opened on a session (or none, from the header). */
  const [requesting, setRequesting] = useState(null);
  const [view, setView] = useState("list");
  const [anchor, setAnchor] = useState(() => weekStart(new Date()));
  /* Tab and filter in the URL (F6), the same convention as the org's class
     page: a tile that opens a pre-filtered tab, a refresh and Back agree. */
  const { tab, filter, setTab } = useTabParam(TABS, "overview");
  const [lessonBar, setLessonBar] = useState({ period: "all", search: "" });

  /* The curriculum revisions and meeting plans on this class. Empty, not an
     error, when the feature is switched off. */
  const loadMaterials = useCallback(async () => {
    const res = await api.get(`/classes/${classId}/materials`).catch(() => ({ data: { materials: [] } }));
    setMaterials(res.data?.materials || []);
  }, [classId]);

  /* The organization's other resources on this class (a classroom layout).
     Empty, not an error, when there are none or the feature is off. */
  const loadResources = useCallback(async () => {
    const res = await api.get(`/classes/${classId}/resources`).catch(() => ({ data: { resources: [] } }));
    setResources(res.data?.resources || []);
  }, [classId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res] = await Promise.all([api.get(`/classes/${classId}/sessions`), loadMaterials(), loadResources()]);
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't load this class"));
    } finally {
      setLoading(false);
    }
  }, [classId, loadMaterials, loadResources]);

  useEffect(() => {
    load();
  }, [load]);

  const cls = data?.class;
  const sessions = useMemo(() => data?.sessions || [], [data]);

  /* Whose Programs this is. The API only ever answers for the caller, so the
     id in the address is normalized to theirs — a placeholder or somebody
     else's would render the caller's class under the wrong door. */
  const owner = useMemo(() => (user?.user_id ? user : { user_id: userId || "me" }), [user, userId]);

  /* A program class lives under its program (I8). Arriving by the old
     address, by a placeholder id, or under the wrong program is sent on to
     the one address that keeps the Programs item lit and Back honest. */
  useEffect(() => {
    if (!cls?.program_id) return;
    const canonical = programClassPath(owner, cls.program_id, classId);
    const stale = !programId || programId !== cls.program_id || (user?.user_id && userId !== user.user_id);
    // The query travels with the redirect: `?tab=lessons&filter=missing` on the
    // old address must land on that tab, not on Overview.
    if (stale) navigate(`${canonical}${search}`, { replace: true });
  }, [cls, owner, programId, userId, user, classId, navigate, search]);

  /* Upcoming ascending (the next one first), past descending (the most recent
     first) — each half read in the direction you actually scan it. */
  const upcoming = useMemo(
    () => sessions.filter((s) => s.upcoming).sort((a, b) => a.date.localeCompare(b.date)),
    [sessions],
  );
  const past = useMemo(
    () => sessions.filter((s) => !s.upcoming).sort((a, b) => b.date.localeCompare(a.date)),
    [sessions],
  );
  // A cleared notice owes nothing (lib/notesClearance); an asked one still does.
  const missing = past.filter((s) => s.status !== "cancelled" && !s.notes && !coveredByOther(s) && !isCleared(s));
  const revisions = useMemo(() => curriculumRevisions(materials), [materials]);
  const ahead = useMemo(() => upcoming.filter((s) => s.status !== "cancelled"), [upcoming]);

  /**
   * What pressing a session does, from anywhere on the page.
   *
   * One destination per state, so the Overview list, the calendar, the Lessons
   * tab and the picker can never send the same meeting to different places: a
   * written session opens its lesson; anything else opens the composer, stamped
   * with the occurrence — program attribution rides the occurrence, and a note
   * composed without it never reaches the organization. Whether the composer
   * opens as a plan or a write-up is its decision, made at the five-minute line.
   */
  const openSession = useCallback(
    (session) => {
      // Covered by someone else: their lesson, read-only, once it exists.
      if (coveredByOther(session)) {
        if (session.covered.lesson_id) navigate(`/lesson/${session.covered.lesson_id}?kind=class`);
        return;
      }
      if (session.notes) {
        navigate(`/lesson/${session.notes.lesson_id}?kind=class`);
        return;
      }
      const href = composeHrefForOccurrence({ ...session, class_id: classId, display_name: data?.class?.name });
      navigate(`${href}&occurrence=${encodeURIComponent(session.occurrence_id)}`);
    },
    [navigate, classId, data],
  );

  /* `?session=YYYY-MM-DD` — the "Add notes" button in a missed-notes alert.
     The email knows the class and the day, not the occurrence's identity, so
     the page that already knows how to open a session does it: straight into
     the composer for that day (or its lesson, if it was written meanwhile),
     replacing this address so Back does not bounce through it. A day that is
     no longer on the class's list lands on the missing-notes view instead of
     guessing. Once per arrival. */
  const sessionParam = useMemo(() => new URLSearchParams(search).get("session"), [search]);
  const handledSession = useRef(null);
  useEffect(() => {
    if (!sessionParam || !data || handledSession.current === sessionParam) return;
    handledSession.current = sessionParam;
    const match = sessions.filter((s) => s.date === sessionParam);
    const session = match.find((s) => s.status !== "cancelled") || match[0];
    if (session && session.status !== "cancelled") {
      if (session.notes) {
        navigate(`/lesson/${session.notes.lesson_id}?kind=class`, { replace: true });
      } else {
        const href = composeHrefForOccurrence({ ...session, class_id: classId, display_name: data?.class?.name });
        navigate(`${href}&occurrence=${encodeURIComponent(session.occurrence_id)}`, { replace: true });
      }
      return;
    }
    toast.error(session ? "That session was cancelled, so there's nothing to write up." : "That session isn't on this class's list any more.");
    setTab("lessons", "missing");
  }, [sessionParam, data, sessions, classId, navigate, setTab]);

  /* The term as one list of rows the shared filter understands (F4): a written
     session is its lesson, a past gap is a missing row, a cancelled meeting is
     cancelled, and a meeting still ahead is scheduled — or planned, once
     something has been written against it. The same words the org's class page
     and the program's Lessons tab filter on. */
  const lessonRows = useMemo(() => {
    const name = data?.class?.name;
    return sessions.map((s) => {
      if (coveredByOther(s)) {
        return {
          lesson_id: s.covered.lesson_id || `covered:${classId}:${s.occurrence_id}`,
          kind: "class",
          class_id: classId,
          covered: s.covered,
          title: name,
          scheduled_date: s.date,
          created_at: `${s.date}T12:00:00`,
          time_local: s.time_local,
          session: s,
        };
      }
      if (s.notes) {
        return {
          lesson_id: s.notes.lesson_id,
          kind: "class",
          class_id: classId,
          title: s.notes.title,
          scheduled_date: s.date,
          created_at: `${s.date}T12:00:00`,
          time_local: s.time_local,
          manual: s.notes.manual,
          written_at: s.notes.written_at,
          session: s,
        };
      }
      if (s.status === "cancelled" || !s.upcoming) {
        return {
          ...missingRow(s, { classId, className: name, status: s.status === "cancelled" ? "cancelled" : undefined }),
          session: s,
        };
      }
      return {
        lesson_id: `scheduled:${classId}:${s.occurrence_id}`,
        kind: "class",
        class_id: classId,
        scheduled: true,
        title: name,
        scheduled_date: s.date,
        created_at: `${s.date}T12:00:00`,
        time_local: s.time_local,
        occurrence_id: s.occurrence_id,
        session: s,
      };
    });
  }, [sessions, classId, data]);
  const lessonStatusFilter = STATUSES.some((st) => st.id === filter) ? filter : "all";
  /* The term top to bottom: what was taught, then what is still to come. */
  const shownLessons = useMemo(
    () => sortChronologically(filterLessons(lessonRows, { ...lessonBar, kind: "all", status: lessonStatusFilter }, () => "")),
    [lessonRows, lessonBar, lessonStatusFilter],
  );
  /* Ticking sessions that need no notes and clearing them, or asking the
     organization to (useInstructorNotesClear). A refresh re-reads the
     sessions only, so the page does not blank. */
  const refreshSessions = useCallback(
    () =>
      api
        .get(`/classes/${classId}/sessions`)
        .then((res) => setData(res.data))
        .catch(() => {}),
    [classId],
  );
  const clearing = useInstructorNotesClear({ rows: shownLessons, onChanged: refreshSessions, testId: "class-notes-clear" });

  /* The term as the shared calendar takes it — the component the org's class
     page and the org calendar draw, in its flat list (D2), so one class looks
     like itself from either portal. Pressing a meeting plans it, writes it up
     or opens its lesson, depending on which side of the line it is. */
  const events = useMemo(
    () =>
      sessions.map((s) => ({
        id: s.occurrence_id,
        date: s.date,
        time_local: s.time_local,
        duration_minutes: s.duration_minutes,
        title: data?.class?.name || "Class",
        subtitle: coveredByOther(s)
          ? `Covered by ${coverName(s.covered)}`
          : s.notes
            ? s.notes.title
            : `${s.attending} ${s.upcoming ? "expected" : "attended"}`,
        color: "var(--accent-yellow)",
        muted: s.status === "cancelled" || coveredByOther(s),
        tags: [
          s.status === "cancelled" ? { label: "No class" } : null,
          coveredByOther(s) ? { label: "Substitute" } : null,
          s.covered?.mine ? { label: "Covering", accent: true } : null,
          s.substitute_request && !s.covered ? REQUEST_STATUS[s.substitute_request.status] || null : null,
          s.status !== "cancelled" && !s.upcoming && !s.notes && !coveredByOther(s) && !isCleared(s)
            ? { label: s.notes_clearance?.status === "requested" ? "Clear requested" : "No notes", accent: true }
            : null,
          s.prep_note ? { label: "Note" } : null,
          s.plan ? { label: "Plan" } : null,
          s.my_prep?.body && !s.notes ? { label: "Prepped" } : null,
        ].filter(Boolean),
        onPress: s.status === "cancelled" || (coveredByOther(s) && !s.covered.lesson_id) ? undefined : () => openSession(s),
      })),
    [sessions, data, openSession],
  );

  if (loading) {
    return <PageShell width="wide" className="font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</PageShell>;
  }
  if (!cls) return null;

  const unrecordable = (cls.member_count || 0) - (cls.recordable_count || 0);
  const taught = past.filter((s) => s.notes && !coveredByOther(s)).length;
  const subs = cls.substitutes || null;
  const address = addressLine(cls.location_address);
  const preppable = ahead.filter((s) => isPrepWindow(s));
  const backTo = cls.program_id ? programDetailPath(owner, cls.program_id) : `/classes/${classId}`;
  const backLabel = cls.program_id ? cls.program_title || "Program" : cls.name;

  return (
    /* The wide shell (F5): this is a list page, and the same gutter either
       side as the org's class page. Prose keeps reading width. */
    <PageShell width="wide">
      <button
        type="button"
        onClick={() => navigate(backTo)}
        className="mb-4 inline-flex items-center gap-1 font-sans text-[13px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
        data-testid="class-sessions-back"
      >
        <Icon name="chevronLeft" size={15} /> {backLabel}
      </button>

      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="font-serif text-2xl font-semibold text-[var(--text-primary)]">{cls.name}</h1>
          {cls.kind && <Tag>{KIND_LABELS[cls.kind] || cls.kind}</Tag>}
          {/* The class's program, as a way to it (the phone's class page links it too). */}
          {cls.program_id && cls.program_title && (
            <Link
              to={programDetailPath(owner, cls.program_id)}
              aria-label={`Open the program ${cls.program_title}`}
              className="rounded-full transition-opacity duration-200 hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent-yellow)]"
              data-testid="class-program-link"
            >
              <Tag accent>{cls.program_title}</Tag>
            </Link>
          )}
          {cls.archived_at && <Tag data-testid="class-archived-tag">Archived</Tag>}
        </div>
        <p className="mt-1 font-sans text-[14px] text-[var(--text-tertiary)]">
          {[cls.meeting_times, cls.org_name].filter(Boolean).join(" · ")}
        </p>
        {/* Always labelled: a meeting you attend happens in the venue's zone,
            and "4:00 PM" alone would be read in whatever zone the reader is in. */}
        {cls.timezone && (
          <p className="mt-1 font-mono text-[12px] text-[var(--text-tertiary)]" data-testid="class-sessions-timezone">
            Times in {cls.timezone.replaceAll("_", " ")}
          </p>
        )}
        {/* The program toggle, stated where it bites rather than in a settings
            page the instructor has no reason to open. */}
        {cls.program_title && !cls.schedule_authoring && (
          <p className="mt-2 inline-flex items-center gap-1.5 font-sans text-[13px] text-[var(--text-tertiary)]">
            <Icon name="lock" size={14} />
            {cls.org_name} sets this schedule. Ask them to move or cancel a session.
          </p>
        )}
        {/* The newest curriculum, one tap from the top; the history is below. */}
        {revisions.length > 0 && (
          <div className="mt-2">
            <a
              href="#curriculum"
              /* The curriculum lives on the Overview; from another tab the
                 anchor would point at nothing. */
              onClick={() => {
                if (tab !== "overview") {
                  setTab("overview");
                  requestAnimationFrame(() => document.getElementById("curriculum")?.scrollIntoView());
                }
              }}
              className="inline-flex items-center gap-1.5 font-sans text-[13px] font-medium text-[var(--accent-yellow-text)] underline-offset-2 hover:underline"
              data-testid="curriculum-quick-link"
            >
              <Icon name="fileText" size={14} /> Curriculum · {revisions[0].filename}
            </a>
          </div>
        )}
      </div>

      {/* Three tabs fit a phone, but the bar scrolls within itself rather than
          push the page sideways if a label ever grows — the org's page does
          the same with its five. */}
      <div className="-mx-4 mb-6 overflow-x-auto px-4 md:mx-0 md:px-0">
        <SegmentedControl
          testId="class-tabs"
          className="whitespace-nowrap"
          value={tab}
          onChange={(id) => setTab(id)}
          options={cls.program_id ? TABS : TABS.filter((t) => t.id !== "updates")}
        />
      </div>

      {/* ---- Overview ------------------------------------------------- */}
      {tab === "overview" && (
        <>
          {/* The vital signs first, in the same place and the same four the
              org's class page uses, each a door into the tab behind its number. */}
          <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="class-tiles">
            <StatCard
              compact
              value={taught}
              label="Sessions taught"
              onPress={() => setTab("lessons", "written")}
              data-testid="class-tile-taught"
            />
            <StatCard
              compact
              value={ahead.length}
              label="Upcoming"
              onPress={() => setTab("calendar")}
              data-testid="class-tile-upcoming"
            />
            {/* Opens the Students tab: the program's roster, narrowed to
                this class, with its export. */}
            <StatCard
              compact
              value={cls.member_count}
              label="Students"
              sub={unrecordable > 0 ? `${unrecordable} without consent` : "all cleared to record"}
              onPress={() => setTab("students")}
              data-testid="class-tile-students"
            />
            <StatCard
              compact
              value={missing.length}
              label="Need notes"
              sub={missing.length ? "past sessions" : "all caught up"}
              onPress={missing.length ? () => setTab("lessons", "missing") : undefined}
              data-testid="class-tile-missing"
            />
          </div>

          {unrecordable > 0 && (
            <Card className="mb-6">
              <div className="flex items-start justify-between gap-4">
                <p className="font-sans text-[14px] leading-relaxed text-[var(--text-secondary)]">
                  {unrecordable} {unrecordable === 1 ? "student is" : "students are"} waiting on a consent answer, so
                  audio recording is off for this class. Typed notes work for everyone and need no consent.
                </p>
                <Button variant="ghost" onClick={() => navigate(`/classes/${classId}`)} data-testid="review-consent">
                  Review
                </Button>
              </div>
            </Card>
          )}

          {/* ---- Upcoming ------------------------------------------------ */}
          {/* Capped at five: a term belongs in the calendar, and what belongs
              here is the next fortnight and the one thing you can do about it
              before the class meets. The row opens the session; five rows each
              ending in a filled button would read as five urgent actions, so
              the only button is on a session already under way. */}
          {/* Two quiet actions beside the heading: plan a session, or ask for
              cover for some (on the program's terms; absent when the
              organization has substitutions off). */}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-x-5 gap-y-1">
            <div className="font-sans text-lg font-semibold tracking-[-0.01em] text-[var(--text-primary)]">Upcoming</div>
            <div className="flex items-center gap-5">
              {subs?.enabled && ahead.length > 0 && (
                <button
                  type="button"
                  onClick={() => setRequesting({ occurrenceId: null })}
                  data-testid="open-request-sub"
                  className="font-sans text-[15px] font-semibold text-[var(--accent-yellow-text)] transition duration-200 active:scale-95"
                >
                  Request a sub
                </button>
              )}
              {preppable.length > 0 && (
                <button
                  type="button"
                  onClick={() => setPicking(true)}
                  data-testid="open-prep-picker"
                  className="font-sans text-[15px] font-semibold text-[var(--accent-yellow-text)] transition duration-200 active:scale-95"
                >
                  + Add prep notes
                </button>
              )}
            </div>
          </div>
          <Card padding={0} className="mb-6">
            {upcoming.length === 0 ? (
              <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">
                No more sessions scheduled.
              </div>
            ) : (
              <>
                {upcoming.slice(0, OVERVIEW_UPCOMING).map((s, i, shown) => {
                  const cancelled = s.status === "cancelled";
                  const other = coveredByOther(s);
                  const asked = !s.covered && s.substitute_request ? REQUEST_STATUS[s.substitute_request.status] : null;
                  return (
                    <div
                      key={s.occurrence_id}
                      className={[
                        i < shown.length - 1 || upcoming.length > OVERVIEW_UPCOMING ? "border-b border-[var(--studio-divider)]" : "",
                        other ? "opacity-60" : "",
                      ].join(" ")}
                    >
                      <ListRow
                        title={sessionLabel(s.date)}
                        subtitle={
                          cancelled
                            ? "Cancelled"
                            : other
                              ? `${clock(s.time_local)} · Covered by ${coverName(s.covered)}`
                              : `${clock(s.time_local)} · ${s.attending} expected`
                        }
                        data-testid={`upcoming-${s.occurrence_id}`}
                        trailing={
                          cancelled ? (
                            <Tag>No class</Tag>
                          ) : other ? (
                            <Tag>Substitute</Tag>
                          ) : isPrepWindow(s) ? (
                            <span className="flex items-center gap-2">
                              {i === 0 && <Tag accent>Next</Tag>}
                              {s.covered?.mine && <Tag accent>Covering</Tag>}
                              {asked && <Tag accent={asked.accent}>{asked.label}</Tag>}
                              {s.my_prep?.body && <Tag>Prepped</Tag>}
                              <Icon name="chevronRight" size={17} className="text-[var(--text-tertiary)]" />
                            </span>
                          ) : (
                            <Button
                              onClick={(e) => {
                                e?.stopPropagation?.();
                                openSession(s);
                              }}
                              data-testid={`session-action-${s.occurrence_id}`}
                            >
                              {prepActionLabel(s)}
                            </Button>
                          )
                        }
                        onPress={cancelled || (other && !s.covered.lesson_id) ? undefined : () => openSession(s)}
                      />
                      {!cancelled && !other && <SessionBrief session={s} className="-mt-1 px-[18px] pb-3" />}
                    </div>
                  );
                })}
                {upcoming.length > OVERVIEW_UPCOMING && (
                  <button
                    type="button"
                    onClick={() => setTab("calendar")}
                    className="block w-full px-[18px] py-3 text-left font-sans text-[13px] text-[var(--accent-yellow-text)]"
                    data-testid="upcoming-view-all"
                  >
                    {upcoming.length - OVERVIEW_UPCOMING} more in the calendar →
                  </button>
                )}
              </>
            )}
          </Card>

          {/* ---- Class details ------------------------------------------- */}
          {/* Where to be and what to know on the way in. The org types it once
              on its own class page; an instructor should not have to hold it in
              their head or go and find the email. */}
          {(address || cls.room || cls.notes) && (
            <>
              <SectionHeader>Class details</SectionHeader>
              <Card className="mb-6" data-testid="class-details">
                <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
                  {[
                    ["Room", cls.room],
                    ["Where", address],
                    ["When", cls.meeting_times],
                    ["Program", cls.program_title],
                  ]
                    .filter(([, v]) => v)
                    .map(([label, value]) => (
                      <div key={label}>
                        <dt className="font-mono text-[11px] uppercase tracking-wide text-[var(--text-tertiary)]">{label}</dt>
                        <dd className="mt-0.5 font-sans text-[14px] text-[var(--text-primary)]">{value}</dd>
                      </div>
                    ))}
                </dl>
                {cls.notes && (
                  <p className="mt-4 whitespace-pre-wrap border-t border-[var(--studio-divider)] pt-4 font-sans text-[14px] leading-relaxed text-[var(--text-secondary)]">
                    {cls.notes}
                  </p>
                )}
              </Card>
            </>
          )}

          <p className="max-w-3xl font-sans text-[13px] leading-relaxed text-[var(--text-tertiary)]">
            Notes written against a session are shared with {cls.org_name || "the organization"} as part of{" "}
            {cls.program_title || "this program"}. Typed notes need no recording consent.
          </p>

          {/* ---- Resources ---------------------------------------------- */}
          {/* The organization's files for this room: tiles, each opening its file. */}
          {resources.length > 0 && (
            <div className="mt-8" data-testid="class-resources">
              <SectionHeader>Resources</SectionHeader>
              <ResourceTiles resources={resources} testId="class-resource-tiles" />
            </div>
          )}

          {/* ---- Curriculum --------------------------------------------- */}
          {/* Every revision, the org's and this instructor's own, newest first. */}
          {cls.program_id && (
            <CurriculumCard
              client={api}
              basePath={`/classes/${classId}`}
              role="instructor"
              actorId={user?.user_id}
              materials={materials}
              onChanged={loadMaterials}
              errorMessage={getErrorMessage}
              className="mt-8"
            />
          )}
        </>
      )}

      {/* ---- Calendar ------------------------------------------------- */}
      {tab === "calendar" &&
        (sessions.length === 0 ? (
          <Card data-testid="class-calendar-empty">
            <p className="font-sans text-[15px] text-[var(--text-tertiary)]">
              No meetings yet — {cls.org_name || "the organization"} hasn&rsquo;t scheduled this class.
            </p>
          </Card>
        ) : (
          <MeetingCalendar
            events={events}
            view={view}
            onViewChange={setView}
            anchor={anchor}
            onAnchorChange={setAnchor}
            emptyText="No class"
            listStyle="flat"
          />
        ))}

      {/* ---- Students ------------------------------------------------ */}
      {/* The program's Students tab, narrowed to this class. */}
      {tab === "students" && <ClassStudentsTab classId={classId} className={cls.name} />}

      {/* ---- Updates -------------------------------------------------- */}
      {tab === "updates" && cls.program_id && (
        <UpdatesInbox programId={cls.program_id} classId={classId} heading="Updates for this class" />
      )}

      {/* ---- Lessons -------------------------------------------------- */}
      {/* The term as a list, the other half of the calendar: the calendar
          answers "when", this answers "what is outstanding". A session never
          written up is a row here rather than an absence, because a meeting
          with no record is precisely a lesson that does not exist and could
          never be found by filtering lessons. Same bar and words as the org's
          class page and the program's Lessons tab (F4). */}
      {tab === "lessons" && (
        <>
          <SectionHeader>
            Lessons ({shownLessons.length})
            {missing.length > 0 && (
              <span
                className="ml-3 font-sans text-[13px] font-medium text-[var(--accent-yellow-text)]"
                data-testid="class-lessons-missing-count"
              >
                {missing.length} missing notes
              </span>
            )}
          </SectionHeader>
          <LessonFilterBar
            value={{ ...lessonBar, status: lessonStatusFilter }}
            onChange={(next) => {
              if (next.status !== lessonStatusFilter) setTab("lessons", next.status === "all" ? undefined : next.status);
              setLessonBar({ period: next.period, search: next.search });
            }}
            searchPlaceholder="Search this class"
          />
          <Card padding={0} data-testid="class-lessons">
            {shownLessons.length > 0 && clearing.toolbar}
            {shownLessons.length === 0 ? (
              <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">
                {lessonRows.length === 0
                  ? `Nothing here yet. Sessions appear as ${cls.org_name || "the organization"} schedules them, and become lessons as you write them up.`
                  : lessonStatusFilter === "missing"
                    ? "Every past session has notes."
                    : "Nothing matches these filters."}
              </div>
            ) : (
              shownLessons.map((row, i) => {
                const st = lessonStatus(row);
                const s = row.session;
                const border = i < shownLessons.length - 1;
                if (st === "covered") {
                  // Someone else's session: greyed, read-only, counted nowhere.
                  return (
                    <div key={row.lesson_id} className="opacity-60">
                      <ListRow
                        borderBottom={border}
                        data-testid={`class-lesson-${row.lesson_id}`}
                        title={sessionLabel(row.scheduled_date)}
                        subtitle={[
                          clock(s.time_local),
                          `Covered by ${coverName(row.covered)}`,
                          row.covered.lesson_id ? "Notes written" : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                        trailing={<Tag>Substitute</Tag>}
                        onPress={row.covered.lesson_id ? () => openSession(s) : undefined}
                      />
                    </div>
                  );
                }
                if (st === "written" || st === "planned") {
                  return (
                    <ListRow
                      key={row.lesson_id}
                      borderBottom={border}
                      data-testid={`class-lesson-${row.lesson_id}`}
                      title={row.title}
                      subtitle={`${sessionLabel(row.scheduled_date)}${row.written_at ? ` · Written ${when(row.written_at)}` : ""}`}
                      trailing={
                        <span className="flex items-center gap-2">
                          {st === "planned" && <Tag>Planned</Tag>}
                          {row.manual && <Tag>Typed</Tag>}
                          <Icon name="check" size={17} className="text-[var(--state-success)]" />
                        </span>
                      }
                      onPress={() => openSession(s)}
                    />
                  );
                }
                if (st === "cancelled") {
                  return (
                    <ListRow
                      key={row.lesson_id}
                      borderBottom={border}
                      data-testid={`class-lesson-${row.lesson_id}`}
                      title={sessionLabel(row.scheduled_date)}
                      subtitle="Cancelled"
                      trailing={<Tag>No class</Tag>}
                    />
                  );
                }
                if (st === "cleared") {
                  // Happened, needs no notes. Notes can still be written for it.
                  return (
                    <ListRow
                      key={row.lesson_id}
                      borderBottom={border}
                      data-testid={`class-lesson-${row.lesson_id}`}
                      leading={clearing.checkbox(row)}
                      title={sessionLabel(row.scheduled_date)}
                      subtitle={[clock(s.time_local), clearanceLine(row.notes_clearance)].filter(Boolean).join(" · ")}
                      trailing={<Tag>Notes cleared</Tag>}
                      onPress={() => openSession(s)}
                    />
                  );
                }
                // Missing, or still ahead. A past gap is the only row that
                // owes something, so it is the only one with a filled button;
                // a meeting still in its prep window says what it is and opens
                // on the row.
                const prepping = st === "scheduled" && isPrepWindow(s);
                const asked = st === "missing" ? clearanceTag(row.notes_clearance) : null;
                return (
                  <ListRow
                    key={row.lesson_id}
                    borderBottom={border}
                    data-testid={`class-lesson-${row.lesson_id}`}
                    leading={st === "missing" ? clearing.checkbox(row) : undefined}
                    title={sessionLabel(row.scheduled_date)}
                    subtitle={[
                      `${clock(s.time_local)} · ${s.attending} ${st === "missing" ? "attended" : "expected"}`,
                      s.prep_note ? "Note from the organization" : null,
                      st === "missing" ? clearanceLine(row.notes_clearance) : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                    trailing={
                      prepping ? (
                        <span className="flex items-center gap-2">
                          {s.my_prep?.body && <Tag>Prepped</Tag>}
                          <Tag>Upcoming</Tag>
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          {asked && <Tag accent>{asked.label}</Tag>}
                          <Button
                            onClick={(e) => {
                              e?.stopPropagation?.();
                              openSession(s);
                            }}
                            data-testid={`write-notes-${s.occurrence_id}`}
                          >
                            {prepActionLabel(s)}
                          </Button>
                        </span>
                      )
                    }
                    onPress={() => openSession(s)}
                  />
                );
              })
            )}
          </Card>
          {clearing.dialogs}
          {data?.complete === false && (
            <p className="mt-3 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="sessions-incomplete">
              Hans couldn&rsquo;t reach every scheduled session, so this list may be missing some.
            </p>
          )}
        </>
      )}

      {subs?.enabled && (
        <RequestSubDialog
          open={Boolean(requesting)}
          onOpenChange={(v) => !v && setRequesting(null)}
          classId={classId}
          className={cls.name}
          sessions={sessions}
          policy={subs}
          initialOccurrenceId={requesting?.occurrenceId || null}
          onRequested={load}
        />
      )}

      <PrepSessionPicker
        open={picking}
        onOpenChange={setPicking}
        sessions={ahead}
        onPick={(session) => {
          setPicking(false);
          openSession(session);
        }}
      />
    </PageShell>
  );
}
